import React, { useEffect, useMemo, useRef, useState } from "react";
import { EXECUTE_AI_AGENT, GET_AI_AGENTS } from "../../api/aiKnowledge.service";
import "./chat-poc.css";

const ChatPOC = () => {
  const [inputValue, setInputValue] = useState("");
  const [chatByAgent, setChatByAgent] = useState({});
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [error, setError] = useState("");
  const messagesEndRef = useRef(null);

  const STORAGE_KEY = "chat_poc_state_v1";

  const activeAgent =
    agents.find((agent) => agent.id === selectedAgentId) || agents[0] || null;

  const activeMessages = useMemo(() => {
    if (!selectedAgentId) return [];
    return chatByAgent[selectedAgentId] || [];
  }, [chatByAgent, selectedAgentId]);

  const nextId = useMemo(
    () => activeMessages.reduce((maxId, msg) => Math.max(maxId, msg.id), 0) + 1,
    [activeMessages],
  );

  const loadAgents = async (preferredAgentId = null) => {
    setIsLoadingAgents(true);
    try {
      const resp = await GET_AI_AGENTS();
      const rows = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.data?.data)
          ? resp.data.data
          : resp?.rows || [];
      const mapped = rows.map((agentItem) => {
        const agent = agentItem?.agent || agentItem || {};
        return {
          id: String(agent.id || agent.agentId || ""),
          label: agent.agentName || "Agent",
          provider: agent.providerName,
          model: agent.llmModel || agent.llmModal,
          status: Number(agent.isActive) === 1 ? "Online" : "Offline",
        };
      });
      setAgents(mapped);

      if (mapped.length > 0) {
        const exists = mapped.find(
          (agent) => agent.id === String(preferredAgentId),
        );
        setSelectedAgentId(exists ? exists.id : mapped[0].id);
      }
    } catch (err) {
      console.error("Failed to load agents", err);
      setError(err?.response?.data?.message || "Failed to load agents.");
    } finally {
      setIsLoadingAgents(false);
    }
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      let preferredAgentId = null;
      let initialChatByAgent = {};
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.chatByAgent && typeof parsed.chatByAgent === "object") {
          initialChatByAgent = parsed.chatByAgent;
        } else if (Array.isArray(parsed?.messages)) {
          // Backward compatibility migration from old shared thread.
          const migratedAgentId = parsed?.selectedAgentId
            ? String(parsed.selectedAgentId)
            : "__migrated__";
          initialChatByAgent = { [migratedAgentId]: parsed.messages };
        }
        if (parsed?.selectedAgentId) {
          preferredAgentId = String(parsed.selectedAgentId);
          setSelectedAgentId(preferredAgentId);
        }
      }
      setChatByAgent(initialChatByAgent);
      loadAgents(preferredAgentId);
    } catch (err) {
      console.error("Failed to load chat state", err);
    }
  }, []);

  useEffect(() => {
    const payload = {
      chatByAgent,
      selectedAgentId,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }, [chatByAgent, selectedAgentId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedAgentId, activeMessages]);

  useEffect(() => {
    if (agents.length === 0) {
      loadAgents(selectedAgentId);
    }
  }, [agents.length, selectedAgentId]);

  useEffect(() => {
    if (!selectedAgentId) return;
    setChatByAgent((prev) => {
      if (prev[selectedAgentId]) return prev;
      return { ...prev, [selectedAgentId]: [] };
    });
  }, [selectedAgentId]);

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || !activeAgent) return;
    if (!selectedAgentId) {
      setError("Please select an agent first.");
      return;
    }

    const userMessage = { id: nextId, role: "user", text: trimmed };
    setChatByAgent((prev) => ({
      ...prev,
      [selectedAgentId]: [...(prev[selectedAgentId] || []), userMessage],
    }));
    setInputValue("");
    setIsSending(true);
    setError("");

    try {
      const payload = {
        query: trimmed,
        agentId: activeAgent.id,
        llmProvider: activeAgent.provider,
        llmModel: activeAgent.model,
      };
      const resp = await EXECUTE_AI_AGENT(payload);
      const replyText =
        resp?.response?.answer ||
        resp?.response ||
        resp?.data?.response ||
        resp?.data?.answer ||
        resp?.data ||
        resp?.message ||
        "Response received.";
      const botMessage = {
        id: nextId + 1,
        role: "assistant",
        text: String(replyText),
      };
      setChatByAgent((prev) => ({
        ...prev,
        [selectedAgentId]: [...(prev[selectedAgentId] || []), botMessage],
      }));
    } catch (err) {
      console.error("Chat API error", err);
      const errorText =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch response.";
      setError(errorText);
      setChatByAgent((prev) => ({
        ...prev,
        [selectedAgentId]: [
          ...(prev[selectedAgentId] || []),
          { id: nextId + 1, role: "assistant", text: errorText },
        ],
      }));
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="chat-poc">
      <div className="chat-poc__panel">
        <div className="chat-poc__panel-header">
          <div className="chat-poc__title">
            <span className="chat-poc__title-text">AI Agent List</span>
          </div>
        </div>

        <div className="chat-poc__contacts">
          {isLoadingAgents && (
            <div className="chat-poc__contact chat-poc__contact--active">
              <div className="chat-poc__contact-avatar">..</div>
              <div className="chat-poc__contact-body">
                <div className="chat-poc__contact-name">Loading agents</div>
                <div className="chat-poc__contact-meta">Please wait</div>
              </div>
            </div>
          )}
          {agents.map((agent) => {
            const isActive = agent.id === selectedAgentId;
            return (
              <button
                key={agent.id}
                type="button"
                className={`chat-poc__contact${
                  isActive ? " chat-poc__contact--active" : ""
                }`}
                onClick={() => setSelectedAgentId(agent.id)}
              >
                <div className="chat-poc__contact-avatar">
                  {agent.label
                    .split(" ")
                    .map((part) => part[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div className="chat-poc__contact-body">
                  <div className="chat-poc__contact-name">{agent.label}</div>
                  <div className="chat-poc__contact-meta">
                    {agent.status}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="chat-poc__chat">
        <div className="chat-poc__chat-header">
          <div className="chat-poc__header-title">
            <div className="chat-poc__header-name">
              {activeAgent ? activeAgent.label : "Agent"}
            </div>
            <div className="chat-poc__header-status">
              <span className="chat-poc__status-dot" />
              {activeAgent ? activeAgent.status : "Offline"}
            </div>
          </div>
          {error ? (
            <div className="chat-poc__header-error">{error}</div>
          ) : null}
        </div>

        <div className="chat-poc__messages">
          {activeMessages.length === 0 ? (
            <div className="chat-poc__empty">No messages for this agent yet.</div>
          ) : (
            activeMessages.map((message) => {
            if (message.role === "user") {
              return (
                <div
                  key={message.id}
                  className="chat-poc__message chat-poc__message--user"
                >
                  <div className="chat-poc__bubble chat-poc__bubble--user">
                    {message.text}
                  </div>
                  <div className="chat-poc__avatar chat-poc__avatar--user">
                    U
                  </div>
                </div>
              );
            }

            if (message.role === "tool" || message.role === "tool_success") {
              return (
                <div key={message.id} className="chat-poc__message">
                  <div className="chat-poc__avatar">A</div>
                  <div
                    className={`chat-poc__tool${
                      message.role === "tool_success"
                        ? " chat-poc__tool--success"
                        : ""
                    }`}
                  >
                    {message.role === "tool" ? "RUN" : "OK"} {message.text}
                  </div>
                </div>
              );
            }

            return (
              <div key={message.id} className="chat-poc__message">
                <div className="chat-poc__avatar">A</div>
                <div className="chat-poc__bubble">{message.text}</div>
              </div>
            );
          })
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-poc__composer">
          <input
            className="chat-poc__input"
            placeholder="Type a message..."
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSending || !activeAgent}
          />
          <button
            type="button"
            className="chat-poc__send-btn"
            onClick={handleSend}
            aria-label="Send message"
            disabled={isSending || !activeAgent}
          >
            &gt;
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPOC;
